/* eslint-disable no-bitwise */

/** Describes one unsigned integer field within a packed word. */
export interface BitFieldSpec {
  /** Index of the field's least significant bit. */
  shift: number;
  /** Number of bits occupied by the field. */
  width: number;
}

/** Pair of adjacent low/high fields produced by {@link createAdjacentBitFieldSpec}. */
export interface AdjacentBitFieldPair {
  low: BitFieldSpec;
  high: BitFieldSpec;
}

/** Builds `{ low, high }` specs for two contiguous unsigned fields in one integer. */
export function createAdjacentBitFieldSpec(lowWidth: number, highWidth: number): AdjacentBitFieldPair {
  return {
    low: { shift: 0, width: lowWidth },
    high: { shift: lowWidth, width: highWidth }
  };
}

/**
 * Returns a bit mask: the low `width` bits set to 1, every higher bit 0.
 * Example: width 4 → binary 1111 (decimal 15); width 8 → binary 11111111 (decimal 255).
 * Callers use it to clamp a value to its field (`value & mask`) and to isolate a field when unpacking.
 */
function maskForWidth(width: number): number {
  if (!Number.isInteger(width) || width < 1 || width > 32) {
    throw new RangeError(`Bit field width must be an integer from 1 to 32, got ${width}`);
  }

  // width 32: every bit set — binary 1111...1 (32 ones), written 0xffffffff.
  // Hardcoded because JS takes the shift count mod 32, so `1 << 32` acts as `1 << 0` = 1;
  // the formula below would then give 1 - 1 = 0 instead of a full 32-bit mask.
  if (width === 32) { return 0xffffffff; }

  // width 31: bits 0-30 set — binary 0111...1 (31 ones), written 0x7fffffff.
  // Hardcoded because `1 << 31` sets JS's signed 32-bit sign bit (the value goes negative),
  // so the formula below would not produce this positive mask.
  if (width === 31) { return 0x7fffffff; }

  // widths 1-30: `1 << width` is 2^width — a 1 followed by `width` zeros (e.g. width 4 → binary 10000).
  // Subtracting 1 flips those zeros to ones, leaving `width` low ones (e.g. 10000 - 1 = 1111 = decimal 15).
  return (1 << width) - 1;
}

/** Packs multiple unsigned fields into one integer using the given specs (order matches `values`). */
export function packBitFields(values: readonly number[], specs: readonly BitFieldSpec[]): number {
  if (values.length !== specs.length) {
    throw new RangeError(`values length (${values.length}) must match specs length (${specs.length})`);
  }
  let packed = 0;
  for (let i = 0; i < specs.length; i++) {
    const { shift, width } = specs[i];
    if (!Number.isInteger(shift) || shift < 0 || shift > 31) {
      throw new RangeError(`Bit field shift must be an integer from 0 to 31, got ${shift}`);
    }

    if (shift + width > 32) {
      throw new RangeError(`Bit field at index ${i} overflows 32 bits (shift=${shift}, width=${width})`);
    }

    // Merge one field into `packed`, in operational order:
    //   1. values[i] & maskForWidth(width): clamp the value to its width so it cannot spill into other fields.
    //   2. << shift:                        slide the clamped bits up to this field's position in the word.
    //   3. packed | ...:                    OR those bits into the accumulator, leaving already-written fields intact.
    //   4. >>> 0:                           reinterpret the result as an unsigned 32-bit integer (keeps the top bit positive).
    packed = (packed | ((values[i] & maskForWidth(width)) << shift)) >>> 0;
  }
  return packed;
}

/** Unpacks one unsigned field from a packed integer. */
export function unpackBitField(packed: number, spec: BitFieldSpec): number {
  // Reverse of the pack merge: `>>> spec.shift` slides the field down to bit 0,
  // then `& maskForWidth` drops every bit above the field, leaving just its value.
  // Trailing `>>> 0` reinterprets the signed `&` result as unsigned, so a width-32 field
  // with bit 31 set returns a positive value instead of a negative int32.
  return ((packed >>> spec.shift) & maskForWidth(spec.width)) >>> 0;
}

/** Unpacks every field described by `specs` from a packed integer (same order as {@link packBitFields}). */
export function unpackBitFields(packed: number, specs: readonly BitFieldSpec[]): number[] {
  return specs.map((spec) => unpackBitField(packed, spec));
}
